/**
 * COC-REPORTING-GRAINS-V1 (activity 21) -- the COC role's clinic-wise +
 * treatment-wise follow-up report (/dashboard/coc/followup-report).
 *
 * NOT a "Reports" hub -- ONE scoped report, the same naming posture the
 * sibling "Daily report" door uses (nav-config.ts's own COC section
 * header: a generic owner-style Reports hub stays DEFERRED / not-built).
 * NOT built onto the Daily report page (a different, in-flight lane owns
 * that page) -- its own standalone door and route.
 *
 * RENDER + ROUTE + ONE NEW BACKEND READ. Unlike the Daily report (zero
 * new backend), this page's numbers are aggregated SERVER-SIDE
 * (GET /api/coc/patient-followups/report ->
 * lib/adapters/supabase/coc-followup-report-read-model.ts) rather than
 * client-side over the already-open Follow-ups list read, because that
 * list is capped (PATIENT_FOLLOWUP_LIST_LIMIT) -- a total computed over a
 * capped list is wrong and looks right. The backend aggregates the SAME
 * ownership-filtered rows the Follow-ups register shows, before that cap
 * ever applies, so a number here is never a partial total passed off as
 * a whole one. Same role gate, same org/clinic fence, same
 * FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1 visibility filter as the register
 * -- this page can never show a case a COC could not otherwise reach.
 *
 * OVERDUE -- exactly ONE definition (patient-treatment-followup overdue,
 * mirroring rowIsOverdue on the Follow-ups register: past the
 * first-follow-up SLA with no recorded contact). This product also
 * carries two OTHER live "overdue" signals -- lead-level SLA breach
 * (the COC station) and assigned-lead overdue (the station manager
 * panel) -- both a different entity (leads, not follow-up rows) and
 * neither shown here; this page never sums the three together and never
 * relabels one as the other (see the run record's overdue-definitions
 * section for all three, verified).
 *
 * ADVISED ITEMS (read-only additive context, never an open opportunity)
 * are shown on the treatment table SEPARATELY from open items, never
 * folded into the open-items count or its estimated value.
 *
 * TREATMENT NAMES are the RAW recorded procedure_name -- no mapping, no
 * normalization, no guessed merge of two names that might be the same
 * treatment (the incumbent-to-product mapping file is a separate, not-
 * yet-delivered tenant dependency; see the run record).
 *
 * NEUTRAL COUNTS: every number here is plain text, never tinted -- the
 * clinic table is pre-sorted worst-first (most overdue cases) by the
 * backend so rank alone shows which clinic is behind (R-STATE-URGENCY-
 * PALETTE-01 governs per-record state/urgency chips; this page renders
 * none -- aggregate counts are not a per-record state).
 */

import { useCallback, useEffect, useState } from 'react';

import { fetchCocFollowupReport } from '@/dashboard/api';
import type {
  CocFollowupClinicReportRowView,
  CocFollowupReportView,
  CocFollowupTreatmentReportRowView,
} from '@/dashboard/api';
import { clinicCountLine, inrLabel, treatmentCountLine } from './coc-followup-report-view';
import {
  AcButton,
  DataTable,
  EmptyState,
  ErrorState,
  KpiTile,
  LoadingState,
  PageHeader,
  SectionBand,
  type DataTableColumn,
} from '@/dashboard/ui';

type ReportLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: CocFollowupReportView };

function clinicColumns(): DataTableColumn<CocFollowupClinicReportRowView>[] {
  return [
    { key: 'clinic', header: 'Clinic', render: (r) => r.clinicLabel },
    { key: 'openCases', header: 'Open cases', align: 'end', render: (r) => String(r.openCases) },
    { key: 'openItems', header: 'Open items', align: 'end', render: (r) => String(r.openItems) },
    {
      key: 'overdue',
      header: 'Overdue (2+ days, no contact)',
      align: 'end',
      render: (r) => String(r.overdueCases),
    },
    {
      key: 'value',
      header: 'Estimated value',
      align: 'end',
      render: (r) => inrLabel(r.estimatedValue),
    },
  ];
}

function treatmentColumns(): DataTableColumn<CocFollowupTreatmentReportRowView>[] {
  return [
    { key: 'treatment', header: 'Treatment', render: (r) => r.procedureName },
    { key: 'openItems', header: 'Open items', align: 'end', render: (r) => String(r.openItems) },
    {
      key: 'value',
      header: 'Estimated value',
      align: 'end',
      render: (r) => inrLabel(r.estimatedValue),
    },
    {
      key: 'advised',
      header: 'Advised (not yet decided)',
      align: 'end',
      render: (r) => String(r.advisedItems),
    },
  ];
}

export default function CocFollowupReportPage(): JSX.Element {
  const [state, setState] = useState<ReportLoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    try {
      const data = await fetchCocFollowupReport();
      setState({ kind: 'ready', data });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Load failed',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === 'error') {
    return (
      <div className="ac-band-flow" data-testid="cfr-page">
        <PageHeader title="Follow-up report" subs={['COC - by clinic and by treatment']} />
        <ErrorState
          title="This report failed to load"
          detail={state.message}
          action={
            <AcButton size="sm" onClick={() => void load()}>
              Retry
            </AcButton>
          }
        />
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div className="ac-band-flow" data-testid="cfr-page">
        <PageHeader title="Follow-up report" subs={['COC - by clinic and by treatment']} />
        <LoadingState label="Loading the follow-up report" />
      </div>
    );
  }

  const { data } = state;
  const hasAnyOpenCases = data.totals.openCases > 0;

  return (
    <div className="ac-band-flow" data-testid="cfr-page">
      <PageHeader
        title="Follow-up report"
        subs={[
          'COC - every open follow-up in your clinics, complete counts (not just what is shown on Follow-ups)',
        ]}
      />

      <div className="cockpit-tile-row" data-testid="cfr-summary-row">
        <KpiTile label="Open cases" value={data.totals.openCases} testId="cfr-tile-open-cases" />
        <KpiTile label="Open items" value={data.totals.openItems} testId="cfr-tile-open-items" />
        <KpiTile
          label="Overdue (2+ days, no contact)"
          value={data.totals.overdueCases}
          testId="cfr-tile-overdue"
        />
        <KpiTile
          label="Estimated value"
          value={inrLabel(data.totals.estimatedValue)}
          testId="cfr-tile-value"
        />
      </div>

      <SectionBand
        title="By clinic"
        basis={clinicCountLine(data.byClinic, data.totals)}
        testId="cfr-clinic-section"
      >
        <DataTable
          columns={clinicColumns()}
          rows={data.byClinic}
          rowKey={(r) => r.clinicId}
          label="Open follow-ups by clinic"
          testId="cfr-clinic-table"
          empty={
            <EmptyState
              title="No open follow-ups"
              body="None of your clinics have an open follow-up right now."
              testId="cfr-clinic-empty"
            />
          }
        />
      </SectionBand>

      <SectionBand
        title="By treatment"
        basis={treatmentCountLine(data.byTreatment, data.totals)}
        testId="cfr-treatment-section"
      >
        <DataTable
          columns={treatmentColumns()}
          rows={data.byTreatment}
          rowKey={(r) => r.procedureName}
          label="Open follow-ups by treatment"
          testId="cfr-treatment-table"
          empty={
            <EmptyState
              title="No open follow-ups"
              body="No treatment has an open follow-up right now."
              testId="cfr-treatment-empty"
            />
          }
        />
      </SectionBand>

      {hasAnyOpenCases ? (
        <p className="panel-caveat" data-testid="cfr-treatment-name-note">
          Treatment names are shown exactly as recorded -- similar names are not yet grouped together.
        </p>
      ) : null}
    </div>
  );
}
